import { useContext } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import AccountPage from './pages/account/account.pages';
import AccountsPage from './pages/accounts/accounts.pages';
import ActivateAccountPage from './pages/activate-account/activate-account.pages';
import AuctionsPage from './pages/auctions/auctions.pages';
import CategoryPage from './pages/category/category.page';
import CategoriesPage from './pages/categories/categories.pages';
import CompaniesPage from './pages/companies/companies.pages';
import CompanyPage from './pages/company/company.pages';
import ConfigurationPage from './pages/configuration/configuration.pages';
import ContentManagementPage from './pages/content-management/content-management.pages';
import CMSPage from './pages/content-management/page/cms-page.pages';
import CMSSection from './pages/content-management/section/cmssection.pages';
import ContributorAccountPage from './pages/role-based/contributor/contributor-account/contributor-account.pages';
import ContributorCompanyPage from './pages/role-based/contributor/contributor-company/contributor-company.pages';
import FulfillmentsPage from './pages/fulfillments/fulfillments.pages.jsx';
import FulfillmentPage from './pages/fulfillment/fulfillment.pages.jsx';
import GiveawayPage from './pages/giveaway/giveaway.page';
import GiveawaysPage from './pages/giveaways/giveaways.pages';
import GrowRoomPage from './pages/grow-room/grow-room.pages';
import IntegrationsPage from './pages/integrations/integrations.pages.jsx';
import InventoriesPage from './pages/inventories/inventories.pages';
import InventoryPage from './pages/inventory/inventory.pages';
import LoginPage from './pages/login/login.pages';
import MediaPage from './pages/media/media.pages';
import MediasPage from './pages/medias/medias.pages';
import MessagePage from './pages/message/message.pages';
import MessagesPage from './pages/messages/messages.pages';
import MetricsPage from './pages/metrics/metrics.pages';
import OrderPage from './pages/order/order.pages';
import OrdersPage from './pages/orders/orders.pages';
import ProductPage from './pages/product/product.pages';
import ProductsPage from './pages/products/products.pages';
import RafflePage from './pages/raffle/raffle.pages';
import RafflesPage from './pages/raffles/raffles.pages';
import ReadyToFulfillPage from './pages/ready-to-fulfill/ready-to-fulfill.pages.jsx';
import SalesPage from './pages/sales/sales.pages';
import ShippingPage from './pages/shipping/shipping.page.jsx';
import ThemesPage from './pages/themes/themes.pages';

import HamburgerMenu from './components/app/hamburger-menu/hamburger-menu.component';
import Header from './components/app/header/header.component';
import Footer from './components/app/footer/footer.component';
import Spinner from './components/reusable/spinner/spinner.component';
import Toasted from './components/reusable/toasted/toasted.component';

import { ConfigurationContext } from './contexts/configuration.context';
import { ToastContext } from './contexts/toast.context';
import { UserContext } from './contexts/user.context';

import { tokenName } from './config.js';

import {
  AppLoadingContainer,
  MainContainer
} from './App.styles';

import './App.css';

function App() {
  const { setAppTheme, configuration } = useContext(ConfigurationContext);
  const { showToast, setShowToast, getToasted, toastError, toastMessage } = useContext(ToastContext);
  const { currentUser } = useContext(UserContext);

  const adminRoutes = () => (
    <Routes>
      <Route index element={<MetricsPage />} />
      <Route path="/accounts" element={<AccountsPage />} />
      <Route path="/accounts/:id" element={<AccountPage />} />
      <Route path="/auctions" element={<AuctionsPage />} />
      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/categories/:id" element={<CategoryPage />} />
      <Route path="/companies" element={<CompaniesPage />} />
      <Route path="/companies/:id" element={<CompanyPage />} />
      <Route path="/configurations" element={<ConfigurationPage />} />
      <Route path="/content-management" element={<ContentManagementPage />} />
      <Route path="/content-management/pages/:id" element={<CMSPage />} />
      <Route path="/content-management/sections/:id" element={<CMSSection />} />
      <Route path="/fulfillments" element={<FulfillmentsPage />} />
      <Route path="/fulfillments/ready" element={<ReadyToFulfillPage />} />
      <Route path="/fulfillments/:id" element={<FulfillmentPage />} />
      <Route path="/giveaways" element={<GiveawaysPage />} />
      <Route path="/giveaways/:id" element={<GiveawayPage />} />
      <Route path="/grow-room" element={<GrowRoomPage />} />
      <Route path='/integrations' element={<IntegrationsPage />} />
      <Route path='/inventories' element={<InventoriesPage />} />
      <Route path='/inventories/:id' element={<InventoryPage />} />
      <Route path='/media' element={<MediasPage />} />
      <Route path='/media/:id' element={<MediaPage />} />
      <Route path='/messages' element={<MessagesPage />} />
      <Route path='/messages/:id' element={<MessagePage />} />
      <Route path="/orders" element={<OrdersPage />} />
      <Route path="/orders/:id" element={<OrderPage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:id" element={<ProductPage />} />
      <Route path="/raffles/:id" element={<RafflePage />} />
      <Route path="/raffles" element={<RafflesPage />} />
      <Route path="/sales" element={<SalesPage />} />
      <Route path="/shipping" element={<ShippingPage />} />
      <Route path="/themes" element={<ThemesPage />} />
    </Routes>
  );

  const contributorRoutes = () => (
    <Routes>
      <Route index element={<ContributorAccountPage />} />
      <Route path="/company" element={<ContributorCompanyPage />} />
      <Route path="/giveaways" element={<GiveawaysPage />} />
      <Route path="/giveaways/:id" element={<GiveawayPage />} />
    </Routes>
  );

  const routes = () => {
    if (localStorage.getItem(tokenName)) {
      return (
        <>
          <HamburgerMenu />
          <div id="page-wrap">
            <Header />
            {!currentUser ? <Spinner /> : currentUser.roleId <= 3 ? adminRoutes() : contributorRoutes()}
            <Footer />
          </div>
        </>
      );
    }

    return (
      <Routes>
        <Route index element={<LoginPage />} />
        <Route path="/accounts/activate/:passwordToken" element={<ActivateAccountPage />} />
      </Routes>
    );
  };

  return (
    <MainContainer id="outer-container" className="App">
      {!configuration ? (
        <AppLoadingContainer>
          <Spinner />
        </AppLoadingContainer>
      ) : (
        <BrowserRouter>
          {routes()}
        </BrowserRouter>
      )}
      <Toasted
        message={toastMessage}
        showToast={showToast}
        setShowToast={setShowToast}
        getToasted={getToasted}
        error={toastError}
      />
    </MainContainer>
  );
}

export default App;